import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Info } from "lucide-react";

const TEMPLATES = [
  { key: "confirmation", label: "Order confirmation", hint: "Sent when a card order is paid or an on-hold order is released." },
  { key: "on_hold", label: "Order on hold", hint: "Sent when a bank-transfer / cash order is placed." },
  { key: "media_ready", label: "Media ready to download", hint: "Sent when media is delivered. Subject default: [short product name] media is ready to download." },
  { key: "abandoned_cart", label: "Abandoned cart reminder", hint: "Sent automatically when a cart is left untouched for 24 hours." },
];
const PLACEHOLDERS = "{customer_name} · {product_name} · {short_product_name} · {order_number} · {download_link} · {cart_items}";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setTmpl = (key, field, v) => setS((prev) => ({ ...prev, templates: { ...prev.templates, [key]: { ...prev.templates[key], [field]: v } } }));

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => setS(false));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        gst_rate: Number(s.gst_rate),
        gst_inclusive: s.gst_inclusive,
        card_surcharge_rate: Number(s.card_surcharge_rate),
        card_surcharge_enabled: s.card_surcharge_enabled,
        brand_logo: s.brand_logo || "",
        templates: s.templates,
      });
      toast.success("Portal options saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (s === null) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#ff2a5f]" /></div>;
  if (s === false) return <div className="py-32 text-center text-zinc-500">Could not load settings.</div>;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-x text-xs uppercase tracking-widest text-zinc-500">Portal Options</div>
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">Settings</h1>
        </div>
        <Button data-testid="save-settings" onClick={save} disabled={busy} className="bg-[#ff2a5f] font-semibold text-white hover:bg-[#e01f50]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save changes</>}
        </Button>
      </div>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-[#0d0d10] p-5">
        <h2 className="font-display text-lg font-bold text-white">Tax &amp; payments</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-zinc-300">GST rate (%)</Label>
            <Input data-testid="settings-gst-rate" type="number" step="0.01" value={(s.gst_rate * 100).toFixed(2)} onChange={(e) => set("gst_rate", Number(e.target.value) / 100)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
            <div><Label className="text-zinc-300">Prices include GST</Label><p className="text-xs text-zinc-500">GST-inclusive pricing</p></div>
            <Switch data-testid="settings-gst-inclusive" checked={s.gst_inclusive} onCheckedChange={(c) => set("gst_inclusive", c)} />
          </div>
          <div>
            <Label className="text-zinc-300">Card surcharge (%)</Label>
            <Input data-testid="settings-surcharge-rate" type="number" step="0.01" value={(s.card_surcharge_rate * 100).toFixed(2)} onChange={(e) => set("card_surcharge_rate", Number(e.target.value) / 100)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
            <div><Label className="text-zinc-300">Card surcharge enabled</Label><p className="text-xs text-zinc-500">Applied to card payments (per-product toggle overrides)</p></div>
            <Switch data-testid="settings-surcharge-enabled" checked={s.card_surcharge_enabled} onCheckedChange={(c) => set("card_surcharge_enabled", c)} />
          </div>
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-[#0d0d10] p-5">
        <h2 className="font-display text-lg font-bold text-white">Branding</h2>
        <div>
          <Label className="text-zinc-300">Brand logo URL</Label>
          <Input data-testid="settings-brand-logo" value={s.brand_logo || ""} onChange={(e) => set("brand_logo", e.target.value)} placeholder="https://… (shown on store cards & checkout)" className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
          {s.brand_logo ? (
            <div className="mt-3 inline-flex items-center rounded-lg border border-zinc-800 bg-zinc-950 p-3">
              <img data-testid="settings-brand-logo-preview" src={s.brand_logo} alt="Brand logo" className="h-10 w-auto object-contain" />
            </div>
          ) : null}
        </div>
      </section>

      <section className="space-y-4 rounded-xl border border-zinc-800 bg-[#0d0d10] p-5">
        <h2 className="font-display text-lg font-bold text-white">Email templates</h2>
        <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-xs text-zinc-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
          <span>Available placeholders: <span className="font-mono-x text-zinc-300">{PLACEHOLDERS}</span></span>
        </div>
        {TEMPLATES.map((t) => (
          <div key={t.key} className="space-y-2 rounded-lg border border-zinc-800 p-4">
            <div className="font-display text-sm font-bold text-white">{t.label}</div>
            <p className="text-xs text-zinc-500">{t.hint}</p>
            <div>
              <Label className="text-zinc-300">Subject</Label>
              <Input data-testid={`tmpl-${t.key}-subject`} value={s.templates?.[t.key]?.subject || ""} onChange={(e) => setTmpl(t.key, "subject", e.target.value)} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Body</Label>
              <Textarea data-testid={`tmpl-${t.key}-body`} value={s.templates?.[t.key]?.body || ""} onChange={(e) => setTmpl(t.key, "body", e.target.value)} rows={6} className="mt-1 border-zinc-700 bg-zinc-950 text-white" />
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
